import React, { useEffect, useState } from 'react';
import { Anchor, Badge, Tooltip, Box, type BadgeProps } from '@mantine/core';
import { pushHashRoute } from '../../utils/hashNavigationUtils';
import { resolveEntityViewHash } from '../../utils/entityViewNavigationUtils';

export interface MultiReferenceEntityBadgeProps {
    entityId: string;
    displayName?: string;
    /** Resolved type on the reference (EntityType). */
    entityType?: string;
    /** Allowed reference types for the field when the item has no EntityType. */
    fallbackEntityTypes?: string[];
    /** View: left-click same tab. Edit: left-click new tab. Middle-click always opens a new tab. */
    leftClickNavigation?: 'sameTab' | 'newTab';
    badgeProps?: BadgeProps;
}

/**
 * Badge for a multi-valued reference item with in-app navigation.
 */
export function MultiReferenceEntityBadge({
    entityId,
    displayName,
    entityType: entityTypeProp,
    fallbackEntityTypes = [],
    leftClickNavigation = 'sameTab',
    badgeProps,
}: MultiReferenceEntityBadgeProps): React.ReactElement {
    const id = (entityId ?? '').trim();
    const entityType = (entityTypeProp || fallbackEntityTypes[0] || '').trim();
    const [viewHash, setViewHash] = useState<string | undefined>();
    const [fullHref, setFullHref] = useState<string | undefined>();

    useEffect(() => {
        if (!entityType || !id) {
            setViewHash(undefined);
            setFullHref(undefined);
            return;
        }
        let cancelled = false;
        resolveEntityViewHash(entityType, id).then((hash) => {
            if (cancelled || !hash || hash === '#') return;
            setViewHash(hash);
            if (typeof window === 'undefined') {
                setFullHref(hash);
                return;
            }
            setFullHref(
                `${window.location.origin}${window.location.pathname}${window.location.search || ''}${hash}`
            );
        });
        return () => {
            cancelled = true;
        };
    }, [entityType, id]);

    const label = displayName || id;
    const canNavigate = !!viewHash && !!fullHref;
    const tooltipLabel = `ID: ${id}`;

    const stopParentActivation = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const openInNewTab = () => {
        if (!fullHref) return;
        window.open(fullHref, '_blank', 'noopener,noreferrer');
    };

    const handlePrimaryClick = (e: React.MouseEvent) => {
        if (!canNavigate || e.button !== 0) return;
        e.preventDefault();
        stopParentActivation(e);
        if (leftClickNavigation === 'newTab') {
            openInNewTab();
            return;
        }
        if (viewHash) {
            pushHashRoute(viewHash);
        }
    };

    const handleMiddleClick = (e: React.MouseEvent) => {
        if (!canNavigate || e.button !== 1) return;
        e.preventDefault();
        stopParentActivation(e);
        openInNewTab();
    };

    const badge = (
        <Badge
            variant="light"
            size="sm"
            {...badgeProps}
            style={{ cursor: canNavigate ? 'pointer' : undefined, ...badgeProps?.style }}
        >
            {label}
        </Badge>
    );

    const content = canNavigate ? (
        <Anchor
            href={fullHref}
            underline="never"
            onClick={handlePrimaryClick}
            onMouseDown={(e) => {
                stopParentActivation(e);
                handleMiddleClick(e);
            }}
            onAuxClick={(e) => {
                if (e.button !== 1) return;
                e.preventDefault();
                stopParentActivation(e);
                openInNewTab();
            }}
        >
            {badge}
        </Anchor>
    ) : (
        badge
    );

    return (
        <Tooltip label={tooltipLabel} withArrow openDelay={300}>
            <Box component="span" display="inline-flex">{content}</Box>
        </Tooltip>
    );
}
